import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal, Eye, RotateCcw, Search, Plus } from 'lucide-react';
import { useDebounce, useUrlTable } from '@/hooks';
import { useWorkspaces, useRestoreWorkspace, useCreateWorkspace } from '@/hooks/api/useWorkspaces';
import { Workspace } from '@/api/workspaces';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import Routes from '@/router/routes';
import { CreateWorkspaceDialog } from '@/components/dialogs/CreateWorkspaceDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead, SortOrder } from '@/components/ui/sortable-table-head';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDate } from '@/lib/date';

interface TableState {
  page: number;
  limit: number;
  search: string;
  includeDeleted: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export default function SuperAdminWorkspaces() {
  const navigate = useNavigate();
  const { state, setPage, setSearch, setFilter, setSort, resetFilters } = useUrlTable<TableState>({
    page: 1,
    limit: 25,
    search: '',
    includeDeleted: 'false',
    sortBy: '',
    sortOrder: 'desc',
  });

  const debouncedSearch = useDebounce(state.search, 300);

  // Build API filters
  const filters = {
    page: state.page,
    limit: state.limit,
    search: debouncedSearch || undefined,
    includeDeleted: state.includeDeleted === 'true',
    sortBy: (state.sortBy || undefined) as 'name' | 'organizationName' | 'createdAt' | 'memberCount' | 'projectCount' | undefined,
    sortOrder: state.sortOrder,
  };

  const handleSort = (sortBy: string, sortOrder: SortOrder) => {
    setSort(sortBy, sortOrder);
  };

  const { data, isLoading, isError } = useWorkspaces(filters);
  const restoreWorkspace = useRestoreWorkspace();
  const createWorkspace = useCreateWorkspace();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  const handleCreateWorkspace = (name: string, ownerEmail: string, organizationId: string) => {
    createWorkspace.mutate(
      { organizationId, payload: { name, ownerEmail } },
      {
        onSuccess: (ws) => {
          setCreateDialogOpen(false);
          navigate(`${Routes.super_admin_dashboard_workspaces.path}/${ws._id}`);
        },
      }
    );
  };

  const handleRestore = (workspace: Workspace, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDialog({
      open: true,
      title: 'Restore Workspace',
      description: `Are you sure you want to restore ${workspace.name}?`,
      onConfirm: () => {
        restoreWorkspace.mutate(
          { id: workspace._id, organizationId: workspace.organizationId },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) }
        );
      },
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Workspaces</h1>
          <p className="text-muted-foreground mt-1">View and manage all workspaces across organizations</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Workspace
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search workspaces..."
                value={state.search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="includeDeleted"
                checked={state.includeDeleted === 'true'}
                onCheckedChange={(checked) =>
                  setFilter('includeDeleted', checked ? 'true' : 'false')
                }
              />
              <label
                htmlFor="includeDeleted"
                className="text-sm text-foreground cursor-pointer select-none"
              >
                Include deleted
              </label>
            </div>
            {(state.search || state.includeDeleted === 'true') && (
              <Button variant="outline" size="sm" onClick={resetFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        {isLoading ? (
          <div className="p-4">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 flex-1" />
                </div>
              ))}
            </div>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-red-600">Failed to load workspaces. Please try again.</p>
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No workspaces found.</p>
            {(state.search || state.includeDeleted === 'true') && (
              <Button variant="link" onClick={resetFilters} className="mt-2">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    sortKey="name"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Name
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="organizationName"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Organization
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="memberCount"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Members
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="projectCount"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Projects
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="createdAt"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Created
                  </SortableTableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((workspace) => (
                  <TableRow
                    key={workspace._id}
                    className="cursor-pointer"
                    onClick={() => navigate(`${Routes.super_admin_dashboard_workspaces.path}/${workspace._id}`)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {workspace.name}
                        {workspace.deletedAt && (
                          <Badge variant="deleted" className="text-xs">
                            Deleted
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{workspace.organizationName}</TableCell>
                    <TableCell>{workspace.memberCount || 0}</TableCell>
                    <TableCell>{workspace.projectCount || 0}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(workspace.createdAt)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`${Routes.super_admin_dashboard_workspaces.path}/${workspace._id}`);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {workspace.deletedAt && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => handleRestore(workspace, e)}>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Restore Workspace
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <div className="text-sm text-muted-foreground">
                {data.totalPages > 1
                  ? <>Showing {(data.page - 1) * data.limit + 1} to{' '}
                    {Math.min(data.page * data.limit, data.total)} of {data.total} workspaces</>
                  : <>Showing {data.total} {data.total === 1 ? 'workspace' : 'workspaces'}</>
                }
              </div>
              {data.totalPages > 1 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(data.page - 1)}
                    disabled={data.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(data.page + 1)}
                    disabled={data.page === data.totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={restoreWorkspace.isPending}
      />

      <CreateWorkspaceDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateWorkspace}
        loading={createWorkspace.isPending}
      />
    </div>
  );
}
